import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthenticationControllerService } from '@chicilon/angular-user-service';
import { LocalStorageEnum } from '@core/enum/local-storage';
import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { logout } from '@state/auth/auth.actions';
import { BehaviorSubject, catchError, EMPTY, filter, Observable, switchMap, take, throwError } from 'rxjs';

export const authInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const accessToken = localStorage.getItem(LocalStorageEnum.ACCESS_TOKEN) as string;
  const shouldSkip = request.url.includes('/refresh-token');
  const lang = localStorage.getItem('lang') || 'vi';

  if (shouldSkip || !accessToken) {
    return next(request);
  }

  const clonedRequest = request.clone({
    headers: request.headers.set('Authorization', `Bearer ${accessToken}`).set('Accept-Language', lang)
  });
  return next(clonedRequest);
};

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const unauthErrorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthenticationControllerService);
  const store = inject(Store<AppState>);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const isAuthRequest = req.url.includes('login') || req.url.includes('refresh');
      const refreshToken = localStorage.getItem(LocalStorageEnum.REFRESH_TOKEN);

      if (error.status === 401 && !isAuthRequest && refreshToken) {
        if (!isRefreshing) {
          isRefreshing = true;
          refreshTokenSubject.next(null);

          return authService.refreshToken(refreshToken).pipe(
            switchMap(refreshResult => {
              isRefreshing = false;

              const accessToken = refreshResult.data?.access_token?.token || '';
              const expiredToken = refreshResult.data?.access_token?.expired_at?.toString() || '';
              const newRefreshToken = refreshResult.data?.refresh_token?.token || '';

              if (accessToken) localStorage.setItem(LocalStorageEnum.ACCESS_TOKEN, accessToken);
              if (expiredToken) localStorage.setItem(LocalStorageEnum.EXPIRED_TOKEN, expiredToken);
              if (newRefreshToken) localStorage.setItem(LocalStorageEnum.REFRESH_TOKEN, newRefreshToken);

              refreshTokenSubject.next(accessToken);

              return next(
                req.clone({
                  headers: req.headers.set('Authorization', `Bearer ${accessToken}`)
                })
              );
            }),
            catchError(refreshError => {
              isRefreshing = false;
              refreshTokenSubject.next(null);

              store.dispatch(logout());

              return throwError(() => refreshError);
            })
          );
        } else {
          return refreshTokenSubject.pipe(
            filter(token => token !== null),
            take(1),
            switchMap(token => {
              return next(
                req.clone({
                  headers: req.headers.set('Authorization', `Bearer ${token}`)
                })
              );
            })
          );
        }
      } else if (error.status === 412) {
        store.dispatch(logout());
        return EMPTY;
      }

      return throwError(() => error);
    })
  );
};
